import { useEffect, useRef, useState } from 'react'

interface ActivityInputAnimatedWrapperProps {
  isHidden?: boolean
  children: React.ReactNode
}

const ANIMATION_DURATION = 300

const ActivityInputAnimatedWrapper = ({
  isHidden = false,
  children
}: ActivityInputAnimatedWrapperProps) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<string>('auto')
  const [overflow, setOverflow] = useState<string>('initial')
  const [opacity, setOpacity] = useState<number>(isHidden ? 0 : 1)
  const [shouldRender, setShouldRender] = useState<boolean>(!isHidden)
  const oldHidden = useRef(isHidden)

  useEffect(() => {
    if (oldHidden.current && !isHidden) {
      oldHidden.current = false

      setShouldRender(true)

      setHeight('0px')
      setOpacity(0)
      setOverflow('hidden')

      setTimeout(() => {
        if (contentRef.current) {
          const targetHeight = contentRef.current.scrollHeight
          setHeight(`${targetHeight}px`)
          setOpacity(1)

          // After animation, set height to auto for dynamic content
          setTimeout(() => {
            setOverflow('initial')
            setHeight('auto')
          }, ANIMATION_DURATION) // Match the CSS transition duration
        }
      }, 0) // Waits for the next frame to check contentRef height
    }

    if (!oldHidden.current && isHidden) {
      oldHidden.current = true

      if (contentRef.current) {
        const currentHeight = contentRef.current.scrollHeight
        setHeight(`${currentHeight}px`)
        setOpacity(1)

        setTimeout(() => {
          setHeight('0px')
          setOpacity(0)

          // After animation, remove from DOM
          setTimeout(() => {
            setShouldRender(false)
          }, ANIMATION_DURATION) // Match the CSS transition duration
        }, 0) // Waits for the next frame to start animation
      }
    }
  }, [isHidden])

  if (!shouldRender) {
    return null
  }

  return (
    <div
      ref={contentRef}
      style={{
        height,
        opacity,
        overflow,
        transition: `height ${ANIMATION_DURATION}ms ease-out, opacity ${ANIMATION_DURATION}ms ease-out`,
        transformOrigin: 'top'
      }}
    >
      {children}
    </div>
  )
}

export default ActivityInputAnimatedWrapper
